import { ExternalLink, LogOut, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUserRoles } from '../../hooks/useUserRoles';
import { logout } from '../../services/auth';
import { resolveCallerRole } from '../users/assignableRoles';
import { roleLabel } from '../users/roleCatalog';
import Loading from './Loading';
import { resolvePortalUrl } from './NoAccess';
import { canUseConsole } from './RoleRoute';

/**
 * The page every role outside tenant administration and platform operations
 * gets instead of the console (owner decision D22). It renders outside the
 * console layout, so no navigation shows and no console request is sent.
 */
export function ConsolePortalPage({ roles }: { roles: string[] }) {
  const { t } = useTranslation();
  const portalUrl = resolvePortalUrl();
  const callerRole = resolveCallerRole(roles);
  const callerRoleLabel = callerRole ? roleLabel((key, fallback) => String(t(key, fallback)), callerRole) : null;

  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-50 p-4 sm:p-6 dark:bg-gray-900">
      <section
        aria-labelledby="console-portal-title"
        className="w-full max-w-xl space-y-4 rounded-lg border border-gray-200 bg-white p-6 sm:p-8 dark:border-gray-700 dark:bg-gray-800"
      >
        <h1
          id="console-portal-title"
          className="flex items-start gap-3 text-xl font-bold text-gray-900 sm:text-2xl dark:text-white"
        >
          <Info className="mt-1 h-6 w-6 flex-shrink-0 text-[#0077BE]" aria-hidden="true" />
          {t('consoleAccess.title', 'Ihre Arbeit erledigen Sie im Portal')}
        </h1>
        <p className="text-sm text-gray-700 dark:text-gray-200">
          {t(
            'consoleAccess.purpose',
            'Die Verwaltungskonsole ist für die Administration Ihres Unternehmens und für den Plattformbetrieb. Hier werden Benutzer und Rollen, Agenten, Integrationen und Einstellungen verwaltet.',
          )}
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-200">
          {callerRoleLabel ? (
            <>
              {t('consoleAccess.yourRole', 'Ihre Rolle: {{role}}.', { role: callerRoleLabel })}{' '}
            </>
          ) : null}
          {t(
            'consoleAccess.portalWork',
            'Mit Ihrer Rolle arbeiten Sie im Portal. Dort finden Sie Ihre Arbeitsbereiche wie Chat, Dokumente und Compliance.',
          )}
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-200">
          {t(
            'consoleAccess.nextStep',
            'Wenn Sie Verwaltungsaufgaben übernehmen sollen, bitten Sie die Administration Ihres Unternehmens um die passende Rolle.',
          )}
        </p>
        {portalUrl ? null : (
          <p className="text-sm text-gray-700 dark:text-gray-200">
            {t('consoleAccess.noPortalLink', 'Öffnen Sie das Portal Ihres Unternehmens über die gewohnte Adresse.')}
          </p>
        )}
        <div className="flex flex-wrap gap-3 pt-2">
          {portalUrl ? (
            <a
              href={portalUrl}
              className="inline-flex items-center gap-2 rounded-lg bg-[#0077BE] px-4 py-2 text-sm font-medium text-white hover:bg-[#00629c] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0077BE]"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              {t('errors.noAccess.toPortal', 'Zum Portal')}
            </a>
          ) : null}
          <button
            type="button"
            onClick={() => {
              void logout();
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {t('auth.logout', 'Abmelden')}
          </button>
        </div>
      </section>
    </main>
  );
}

/**
 * Wraps the whole console (layout and every route). Roles that may not use
 * the console get the portal page at any URL, so neither the navigation nor
 * a single page or request of the console is reachable for them.
 */
export default function ConsoleAccessGuard({ children }: { children: JSX.Element }) {
  const roles = useUserRoles();
  if (roles.isLoading) return <Loading />;
  if (!canUseConsole(roles)) return <ConsolePortalPage roles={roles.roles} />;
  return children;
}
